// @owners { team: patients }
import { Toast } from '@alto/design-system';
import React, { type ReactElement } from 'react';
import { openCart } from '~shared/actions/ui/cart';
import { Sentry } from '~shared/sentry';
import { type ReduxDispatchShared } from '~shared/types';
import { ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../constants';

export type UpdateCartLike = (params: {
  resource_id: number;
  resource_type: 'Prescription';
  auto_refill_enabled: boolean;
}) => Promise<{ success: boolean }>;

export type AddToastFn = (toast: ReactElement) => void;

export const persistCartOrderType = async ({
  handleUpdateCartItem,
  prescriptionID,
  autoRefillEnabled,
  addToast,
}: {
  handleUpdateCartItem: UpdateCartLike;
  prescriptionID: number;
  autoRefillEnabled: boolean;
  addToast: AddToastFn;
}): Promise<boolean> => {
  const res = await handleUpdateCartItem({
    resource_id: prescriptionID,
    resource_type: 'Prescription',
    auto_refill_enabled: autoRefillEnabled,
  });
  if (!res.success) {
    addToast(<Toast variant="error">There was an error updating the order type. Please try again later.</Toast>);
  }
  return res.success;
};

export const persistAddToCartOrderType = async ({
  handleUpdateCartItem,
  handleAddToCart,
  isPrescriptionInCart,
  prescriptionID,
  autoRefillEnabled,
  addToast,
  dispatch,
  isCartShowing,
}: {
  handleUpdateCartItem: UpdateCartLike;
  handleAddToCart: UpdateCartLike;
  isPrescriptionInCart: (id: number) => boolean;
  prescriptionID: number;
  autoRefillEnabled: boolean;
  addToast: AddToastFn;
  dispatch: ReduxDispatchShared;
  isCartShowing: boolean;
}): Promise<boolean> => {
  const res = isPrescriptionInCart(prescriptionID)
    ? await handleUpdateCartItem({
        resource_id: prescriptionID,
        resource_type: 'Prescription',
        auto_refill_enabled: autoRefillEnabled,
      })
    : await handleAddToCart({
        resource_id: prescriptionID,
        resource_type: 'Prescription',
        auto_refill_enabled: autoRefillEnabled,
      });

  if (!res.success) {
    addToast(<Toast variant="error">There was an error adding to your cart. Please try again later.</Toast>);
    return false;
  }

  if (!isCartShowing) {
    dispatch(openCart());
  }
  return true;
};

export const persistPostCheckoutOrderType = async ({
  updateServerAutoRefillStatus,
  invalidatePrescriptionsQuery,
  invalidateMedicationQuery,
  prescriptionID,
  autoRefillEnabled,
  addToast,
}: {
  updateServerAutoRefillStatus: (enabled: boolean) => Promise<boolean>;
  invalidatePrescriptionsQuery: (id: number) => void;
  invalidateMedicationQuery: (id: number) => void;
  prescriptionID: number;
  autoRefillEnabled: boolean;
  addToast: AddToastFn;
}): Promise<boolean> => {
  const success = await updateServerAutoRefillStatus(autoRefillEnabled);

  if (!success) {
    addToast(<Toast variant="error">There was an error updating the order type. Please try again later.</Toast>);
    return false;
  }

  invalidatePrescriptionsQuery(prescriptionID);
  invalidateMedicationQuery(prescriptionID);

  return true;
};

export type PersistOrderTypeForContextParams = {
  orderTypeContext: OrderTypeContext;
  handleUpdateCartItem: UpdateCartLike;
  handleAddToCart: UpdateCartLike;
  isPrescriptionInCart: (id: number) => boolean;
  prescriptionID: number;
  autoRefillEnabled: boolean;
  addToast: AddToastFn;
  dispatch: ReduxDispatchShared;
  isCartShowing: boolean;
  updateServerAutoRefillStatus: (enabled: boolean) => Promise<boolean>;
  invalidatePrescriptionsQuery: (id: number) => void;
  invalidateMedicationQuery: (id: number) => void;
};

export const persistOrderTypeForContext = async (params: PersistOrderTypeForContextParams): Promise<boolean> => {
  const {
    orderTypeContext,
    handleUpdateCartItem,
    handleAddToCart,
    isPrescriptionInCart,
    prescriptionID,
    autoRefillEnabled,
    addToast,
    dispatch,
    isCartShowing,
    updateServerAutoRefillStatus,
    invalidatePrescriptionsQuery,
    invalidateMedicationQuery,
  } = params;

  switch (orderTypeContext) {
    case ORDER_TYPE_CONTEXT.CART:
      return persistCartOrderType({
        handleUpdateCartItem,
        prescriptionID,
        autoRefillEnabled,
        addToast,
      });
    case ORDER_TYPE_CONTEXT.ADD_TO_CART:
      return persistAddToCartOrderType({
        handleUpdateCartItem,
        handleAddToCart,
        isPrescriptionInCart,
        prescriptionID,
        autoRefillEnabled,
        addToast,
        dispatch,
        isCartShowing,
      });
    case ORDER_TYPE_CONTEXT.POST_CHECKOUT:
      return persistPostCheckoutOrderType({
        updateServerAutoRefillStatus,
        invalidatePrescriptionsQuery,
        invalidateMedicationQuery,
        prescriptionID,
        autoRefillEnabled,
        addToast,
      });
    default:
      Sentry.captureException(
        new Error(`[persistOrderTypeForContext] Unhandled order type context: ${orderTypeContext}`),
      );
      return false;
  }
};
